import { flexRender, Table } from '@tanstack/react-table'
import { Skeleton, Td, TRow } from '@tecsinapse/cortex-react'
import React from 'react'
import { Link } from 'react-router-dom'
import { keyExtractor } from '../utils/key-extractor-route'

interface BodyProps<T> {
  table: Table<T>
  onClickRow?: (row: T) => void
  onDoubleClickRow?: (row: T) => void
  id?: string
  loading?: boolean
  prefix?: string | ((row: T) => string)
  dataName?: string
  skipFirstColumns?: number
}

const dummyArray = (size = 5) =>
  Array.from({ length: size }, (_, index) => ({ id: index }))

export const Body = <T,>({
  table,
  onClickRow,
  onDoubleClickRow,
  id,
  loading,
  prefix = '',
  dataName,
  skipFirstColumns = 0
}: BodyProps<T>) => {
  return (
    <tbody id={id}>
      {loading
        ? dummyArray(table.getState().pagination.pageSize).map((_, index) => (
            <TRow key={index}>
              <Td colSpan={99}>
                <Skeleton key={index} className='h-peta w-full rounded-mili' />
              </Td>
            </TRow>
          ))
        : table.getRowModel().rows.map(row => (
            <TRow
              key={row.id}
              onClick={() => onClickRow?.(row.original)}
              onDoubleClick={() => onDoubleClickRow?.(row.original)}
              className={
                onClickRow || onDoubleClickRow ? 'hover:cursor-pointer' : ''
              }
            >
              {row.getVisibleCells().map((cell, index, cells) => {
                const isFirstSkipped = index < skipFirstColumns
                const isLast = index === cells.length - 1
                const resolvedPrefix =
                  typeof prefix === 'function'
                    ? prefix(keyExtractor(row.original, dataName))
                    : prefix
                return (
                  <Td
                    key={cell.id}
                    className={cell.column.getSize() === 20 ? 'p-0' : 'p-deca'}
                  >
                    {!isFirstSkipped && !isLast && prefix !== '' ? (
                      <Link
                        to={resolvedPrefix}
                        onClick={e => e.stopPropagation()}
                        className='flex w-full h-full items-center text-inherit no-underline'
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Link>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </Td>
                )
              })}
            </TRow>
          ))}
    </tbody>
  )
}
